import { useEffect, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { getPrivateCustomersLabel, isDistributorRole } from '../utils/operator';
import { TARIFA_OPTIONS } from '../../../lib/utils/tarifas';

type CustomerInfo = {
  name: string;
  email: string;
  phone?: string;
  nif?: string;
};

type RecipientInfo = {
  name: string;
  email?: string;
  phone?: string;
  notes?: string;
};

interface CreateOrderModalProps {
  open: boolean;
  onClose: () => void;
  defaultCustomer?: CustomerInfo;
}

const STEPS = ['Cliente', 'Productos', 'Envío y confirmación'] as const;

const getTarifaLabel = (tarifa?: number) => {
  if (!tarifa) return null;
  const opt = TARIFA_OPTIONS.find(t => t.value === tarifa);
  return opt ? opt.shortLabel : `Tarifa ${tarifa}`;
};

const CreateOrderModal = ({ open, onClose, defaultCustomer }: CreateOrderModalProps) => {
  const { auth } = useAuth();
  const isDistributor = isDistributorRole(auth.user?.role);
  const isCommercial = auth.user?.role === 'commercial' || auth.user?.role === 'admin' || auth.user?.role === 'beeadmin';

  // Wizard state
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Client
  const [registeredClients, setRegisteredClients] = useState<any[]>([]);
  const [privateClients, setPrivateClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedClientType, setSelectedClientType] = useState<'registered' | 'private' | null>(null);
  const [selectedClientTarifa, setSelectedClientTarifa] = useState<number | undefined>(undefined);
  const [customer, setCustomer] = useState<CustomerInfo>({
    name: defaultCustomer?.name || '',
    email: defaultCustomer?.email || '',
    phone: defaultCustomer?.phone || '',
    nif: defaultCustomer?.nif || ''
  });

  // Drop-shipping (distributor)
  const [dropShippingEnabled, setDropShippingEnabled] = useState(isDistributor);
  const [notifyRecipient, setNotifyRecipient] = useState(false);
  const [recipient, setRecipient] = useState<RecipientInfo>({ name: '', email: '', phone: '', notes: '' });

  // Step 2: Products
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [items, setItems] = useState<Array<{
    productId: string;
    name?: string;
    quantity: number;
    price?: number;
    packaging?: { minimumQuantity: number; quantityStep: number; presentation?: string };
  }>>([]);
  const [productPrices, setProductPrices] = useState<Record<string, number>>({});

  // Step 3: Shipping & payment
  const [shipping, setShipping] = useState({ street: '', city: '', postalCode: '', country: 'España' });
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [shippingQuote, setShippingQuote] = useState<{ shippingCost: number; currency: string; etaDays: number } | null>(null);
  const [orderNotes, setOrderNotes] = useState('');

  useEffect(() => {
    setDropShippingEnabled(isDistributor);
  }, [isDistributor]);

  // Fetch clients on open
  useEffect(() => {
    const fetchClients = async () => {
      if (!open) return;
      setLoadingClients(true);
      try {
        if (auth.user?.role === 'commercial') {
          const regRes = await api.users.getMyClients();
          if (regRes.success && regRes.data) setRegisteredClients(regRes.data);
        } else {
          setRegisteredClients([]);
        }
        if (auth.user?.role === 'commercial' || auth.user?.role === 'distributor') {
          const privRes = await api.users.getMyPrivateClients();
          if (privRes.success && privRes.data) setPrivateClients(privRes.data);
        } else {
          setPrivateClients([]);
        }
        if (isDistributor) {
          const settingsRes = await api.auth.getDropShippingEmailSettings();
          if (settingsRes.success && settingsRes.data) {
            setNotifyRecipient(settingsRes.data.recipientEmailsEnabledByDefault);
          }
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, [open, isCommercial, isDistributor, auth.user?.role]);

  // Product search
  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!productQuery || productQuery.trim().length < 2) {
        if (active) setProductResults([]);
        return;
      }
      setSearching(true);
      try {
        const res = await api.products.searchProducts(productQuery);
        if (active) setProductResults(res.success ? (res.data || []) : []);
      } catch {
        if (active) setProductResults([]);
      } finally {
        if (active) setSearching(false);
      }
    };
    const t = setTimeout(run, 250);
    return () => { active = false; clearTimeout(t); };
  }, [productQuery]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep(1);
      setError(null);
      setSelectedClientId('');
      setSelectedClientType(null);
      setSelectedClientTarifa(undefined);
      setItems([]);
      setProductQuery('');
      setProductResults([]);
      setShippingQuote(null);
    }
  }, [open]);

  if (!open) return null;

  // --- Client selection ---
  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    if (!clientId) {
      setSelectedClientType(null);
      setSelectedClientTarifa(undefined);
      setCustomer({ name: '', email: '', phone: '', nif: '' });
      setRecipient({ name: '', email: '', phone: '', notes: '' });
      setShipping({ street: '', city: '', postalCode: '', country: 'España' });
      return;
    }

    // Check registered clients first
    let selected = registeredClients.find((c: any) => c.id === clientId || c._id === clientId);
    if (selected) {
      setSelectedClientType('registered');
      setSelectedClientTarifa(selected.tarifa);
      if (isDistributor) {
        setRecipient({ name: selected.name || '', email: selected.email || '', phone: selected.phone || '', notes: selected.notes || '' });
      } else {
        setCustomer({ name: selected.name || '', email: selected.email || '', phone: selected.phone || '', nif: selected.nif || '' });
      }
      if (selected.address) {
        setShipping({ street: selected.address.street || '', city: selected.address.city || '', postalCode: selected.address.postalCode || '', country: selected.address.country || 'España' });
      }
      return;
    }

    // Check private clients
    selected = privateClients.find((c: any) => c._id === clientId);
    if (selected) {
      setSelectedClientType('private');
      setSelectedClientTarifa(selected.tarifa);
      if (isDistributor) {
        setRecipient({ name: selected.name || '', email: selected.email || '', phone: selected.phone || '', notes: selected.notes || '' });
      } else {
        setCustomer({ name: selected.name || '', email: selected.email || '', phone: selected.phone || '', nif: selected.nif || '' });
      }
      if (selected.address) {
        setShipping({ street: selected.address.street || '', city: selected.address.city || '', postalCode: selected.address.postalCode || '', country: selected.address.country || 'España' });
      }
    }
  };

  // --- Products ---
  const addProduct = (product: any) => {
    const price = product?.price || 0;
    const variant = product?.variants?.[0];
    const packaging = {
      minimumQuantity: variant?.packaging?.minimumQuantity || 1,
      quantityStep: variant?.packaging?.quantityStep || 1,
      presentation: variant?.packaging?.presentation || ''
    };

    setItems(prev => {
      const existing = prev.find(p => p.productId === product.id);
      if (existing) {
        const qtyStep = existing.packaging?.quantityStep || 1;
        return prev.map(p => p.productId === product.id ? { ...p, quantity: p.quantity + qtyStep } : p);
      }
      return [...prev, { productId: product.id, name: product.name, quantity: packaging.minimumQuantity, price, packaging }];
    });

    setProductPrices(prev => ({ ...prev, [product.id]: price }));
    setProductQuery('');
    setProductResults([]);
  };

  const updateQty = (pid: string, qty: number) => {
    const item = items.find(i => i.productId === pid);
    const min = item?.packaging?.minimumQuantity || 1;
    const qtyStep = item?.packaging?.quantityStep || 1;
    const roundedQty = Math.max(min, Math.ceil(qty / qtyStep) * qtyStep);
    setItems(prev => prev.map(p => p.productId === pid ? { ...p, quantity: roundedQty } : p));
  };

  const removeItem = (pid: string) => {
    setItems(prev => prev.filter(p => p.productId !== pid));
  };

  // --- Totals ---
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      const price = item.price || productPrices[item.productId] || 0;
      return sum + (price * item.quantity);
    }, 0);
    const shippingCost = shippingQuote?.shippingCost || 0;
    return { subtotal, shippingCost, total: subtotal + shippingCost };
  };

  // --- Shipping quote ---
  const calcShipping = async () => {
    try {
      const res = await api.orders.shippingQuote({
        items: items.map(i => ({ productId: i.productId, quantity: i.quantity })),
        shippingAddress: { country: shipping.country, postalCode: shipping.postalCode, city: shipping.city, street: shipping.street }
      });
      if (res.success && res.data) setShippingQuote(res.data);
    } catch {
      setShippingQuote(null);
    }
  };

  // --- Step validation ---
  const validateStep1 = (): string | null => {
    if (isCommercial) {
      if (!customer.name || !customer.email) return 'El nombre y email del cliente son obligatorios';
      const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/;
      if (!emailRegex.test(customer.email)) return 'Email inválido';
    }
    if (isDistributor && dropShippingEnabled) {
      if (!recipient.name) return 'El nombre del destinatario final es obligatorio';
      if (notifyRecipient && !recipient.email) return 'El email del destinatario es obligatorio si quieres notificarle';
    }
    return null;
  };

  const validateStep2 = (): string | null => {
    if (items.length === 0) return 'Añade al menos un producto';
    return null;
  };

  const validateStep3 = (): string | null => {
    if (!shipping.street || !shipping.city || !shipping.postalCode) return 'La dirección de envío completa es obligatoria';
    return null;
  };

  const goNext = () => {
    setError(null);
    const validators = [validateStep1, validateStep2, validateStep3];
    const err = validators[step - 1]();
    if (err) { setError(err); return; }
    setStep(s => Math.min(s + 1, 3) as 1 | 2 | 3);
  };

  const goBack = () => {
    setError(null);
    setStep(s => Math.max(s - 1, 1) as 1 | 2 | 3);
  };

  // --- Submit ---
  const createOrder = async () => {
    setError(null);
    const err3 = validateStep3();
    if (err3) { setError(err3); return; }

    setSubmitting(true);
    try {
      const payload: any = {
        products: items.map(i => ({ productId: i.productId, quantity: i.quantity })),
        shippingAddress: shipping,
        shippingMethod,
        paymentMethod,
        ...(orderNotes.trim() ? { notes: orderNotes.trim() } : {}),
        ...(isCommercial ? { customerInfo: customer } : {}),
        ...(selectedClientType === 'registered' && selectedClientId ? { customerId: selectedClientId } : {}),
        ...(selectedClientType === 'private' && selectedClientId ? { privateCustomerId: selectedClientId } : {}),
        ...(isDistributor && dropShippingEnabled ? {
          dropShipping: {
            enabled: true,
            notifyRecipient,
            recipient: { ...recipient, address: shipping }
          }
        } : {})
      };

      const resp = await api.orders.createOrder(payload);
      if (!resp.success) {
        setError(resp.error?.message || 'No se pudo crear el pedido');
      } else {
        onClose();
        setItems([]);
        setOrderNotes('');
      }
    } catch (e: any) {
      setError(e?.message || 'Error al crear el pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const tarifaLabel = getTarifaLabel(selectedClientTarifa) || getTarifaLabel(auth.user?.tarifa) || 'Tu tarifa';

  // --- RENDER ---
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="text-lg font-semibold">Crear pedido</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {/* Progress bar */}
        <div className="px-6 pb-4">
          <div className="flex items-center gap-1">
            {STEPS.map((label, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isDone = step > stepNum;
              return (
                <div key={label} className="flex-1 flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                    isActive ? 'bg-black text-white' : isDone ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {isDone ? '✓' : stepNum}
                  </div>
                  <span className={`text-xs truncate ${isActive ? 'font-medium text-black' : 'text-gray-500'}`}>{label}</span>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-px ${isDone ? 'bg-green-600' : 'bg-gray-200'}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Error */}
        {error && <div className="mx-6 mb-3 text-red-600 text-sm bg-red-50 px-3 py-2 rounded">{error}</div>}

        <div className="px-6 pb-6 space-y-4">

          {/* =================== STEP 1: CLIENT =================== */}
          {step === 1 && (
            <>
              {/* Client selector */}
              {(registeredClients.length > 0 || privateClients.length > 0) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isDistributor ? 'Seleccionar destinatario guardado' : 'Seleccionar cliente'}
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => handleClientSelect(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm"
                    disabled={loadingClients}
                  >
                    <option value="">{isDistributor ? '-- Nuevo destinatario --' : '-- Introducir datos manualmente --'}</option>
                    {!isDistributor && registeredClients.length > 0 && (
                      <optgroup label="Clientes registrados">
                        {registeredClients.map((c: any) => (
                          <option key={c.id || c._id} value={c.id || c._id}>
                            {c.name} ({c.email}){c.tarifa ? ` · Tarifa ${c.tarifa}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {privateClients.length > 0 && (
                      <optgroup label={getPrivateCustomersLabel(auth.user?.role)}>
                        {privateClients.map((c: any) => (
                          <option key={c._id} value={c._id}>
                            {c.name} {c.email ? `(${c.email})` : ''}{c.tarifa ? ` · Tarifa ${c.tarifa}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {loadingClients && <div className="text-xs text-gray-500 mt-1">Cargando clientes...</div>}
                </div>
              )}

              {/* Selected client summary card */}
              {selectedClientId && (
                <div className="bg-gray-50 border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{isDistributor ? recipient.name : customer.name}</div>
                      <div className="text-xs text-gray-500">{isDistributor ? recipient.email : customer.email}</div>
                    </div>
                    {selectedClientTarifa && (
                      <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        Tarifa {selectedClientTarifa}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Manual customer info */}
              {isCommercial && (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700">
                    {selectedClientId ? 'Datos del cliente' : 'Datos del cliente (manual)'}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Nombre <span className="text-red-500">*</span></label>
                      <input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Nombre del cliente" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Email <span className="text-red-500">*</span></label>
                      <input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="email@ejemplo.com" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Telefono</label>
                      <input value={customer.phone || ''} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Opcional" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">NIF</label>
                      <input value={customer.nif || ''} onChange={(e) => setCustomer({ ...customer, nif: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Opcional" />
                    </div>
                  </div>
                </div>
              )}

              {/* Drop-shipping section for distributors */}
              {isDistributor && (
                <div className="border rounded-lg p-4 bg-amber-50 space-y-3">
                  <label className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-800">Envio directo al cliente final</span>
                    <input type="checkbox" checked={dropShippingEnabled} onChange={(e) => setDropShippingEnabled(e.target.checked)} className="h-4 w-4 rounded" />
                  </label>

                  {dropShippingEnabled && (
                    <>
                      <label className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Enviar email logistico al destinatario</span>
                        <input type="checkbox" checked={notifyRecipient} onChange={(e) => setNotifyRecipient(e.target.checked)} className="h-4 w-4 rounded" />
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Destinatario <span className="text-red-500">*</span></label>
                          <input value={recipient.name} onChange={(e) => setRecipient({ ...recipient, name: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" placeholder="Nombre del cliente final" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Email {notifyRecipient && <span className="text-red-500">*</span>}</label>
                          <input type="email" value={recipient.email || ''} onChange={(e) => setRecipient({ ...recipient, email: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" placeholder="email@cliente.com" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Telefono</label>
                          <input value={recipient.phone || ''} onChange={(e) => setRecipient({ ...recipient, phone: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" placeholder="Opcional" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Notas logistica</label>
                          <input value={recipient.notes || ''} onChange={(e) => setRecipient({ ...recipient, notes: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" placeholder="Opcional" />
                        </div>
                      </div>
                      <p className="text-xs text-amber-800">
                        El pedido pertenece al distribuidor. El cliente final solo recibe comunicacion logistica si lo activas.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Tarifa info */}
              <div className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                Los precios del pedido se calcularan con <span className="font-medium text-gray-700">{tarifaLabel}</span>
                {selectedClientTarifa ? ' (tarifa del cliente seleccionado)' : ''}
              </div>
            </>
          )}

          {/* =================== STEP 2: PRODUCTS =================== */}
          {step === 2 && (
            <>
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Buscar producto</label>
                <input
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2.5 text-sm"
                  placeholder="Escribe para buscar por nombre..."
                  autoFocus
                />
                {searching && <div className="text-xs text-gray-500 mt-1">Buscando...</div>}
                {productResults.length > 0 && (
                  <div className="mt-1 max-h-48 overflow-auto border rounded-lg bg-white shadow-sm">
                    {productResults.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => addProduct(p)}
                        className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b last:border-b-0 flex items-center justify-between"
                      >
                        <div>
                          <div className="text-sm font-medium">{p.name}</div>
                          {p.variants?.[0]?.packaging?.presentation && (
                            <div className="text-xs text-gray-400">{p.variants[0].packaging.presentation}</div>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 font-medium">{p.price?.toFixed?.(2)}&euro;</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Items list */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Productos del pedido ({items.length})</div>
                {items.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-6 border rounded-lg border-dashed">
                    Busca y selecciona productos para el pedido
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((it) => {
                      const price = it.price || productPrices[it.productId] || 0;
                      const lineTotal = price * it.quantity;
                      return (
                        <div key={it.productId} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2.5">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{it.name || it.productId}</div>
                            <div className="text-xs text-gray-500">{price.toFixed(2)}&euro; x {it.quantity} = <span className="font-medium">{lineTotal.toFixed(2)}&euro;</span></div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button onClick={() => updateQty(it.productId, it.quantity - (it.packaging?.quantityStep || 1))} className="w-7 h-7 border rounded flex items-center justify-center text-gray-500 hover:bg-gray-50">-</button>
                            <input
                              type="number"
                              min={it.packaging?.minimumQuantity || 1}
                              step={it.packaging?.quantityStep || 1}
                              className="w-16 border rounded px-2 py-1 text-sm text-center"
                              value={it.quantity}
                              onChange={(e) => updateQty(it.productId, parseInt(e.target.value || '1'))}
                            />
                            <button onClick={() => updateQty(it.productId, it.quantity + (it.packaging?.quantityStep || 1))} className="w-7 h-7 border rounded flex items-center justify-center text-gray-500 hover:bg-gray-50">+</button>
                            <button onClick={() => removeItem(it.productId)} className="text-xs text-red-500 hover:text-red-700 ml-1">Quitar</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Subtotal */}
              {items.length > 0 && (
                <div className="flex justify-between text-sm font-medium border-t pt-3">
                  <span>Subtotal productos:</span>
                  <span>{calculateTotals().subtotal.toFixed(2)}&euro;</span>
                </div>
              )}
            </>
          )}

          {/* =================== STEP 3: SHIPPING & CONFIRMATION =================== */}
          {step === 3 && (
            <>
              {/* Shipping address */}
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Direccion de envio</div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-3">
                    <label className="block text-xs text-gray-600 mb-1">Calle <span className="text-red-500">*</span></label>
                    <input value={shipping.street} onChange={(e) => setShipping({ ...shipping, street: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Direccion completa" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Ciudad <span className="text-red-500">*</span></label>
                    <input value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Ciudad" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">CP <span className="text-red-500">*</span></label>
                    <input value={shipping.postalCode} onChange={(e) => setShipping({ ...shipping, postalCode: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="28001" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Pais</label>
                    <input value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Espana" />
                  </div>
                </div>
              </div>

              {/* Shipping & payment */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Metodo de envio</label>
                  <select value={shippingMethod} onChange={(e) => setShippingMethod(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="standard">Estandar</option>
                    <option value="express">Express</option>
                    <option value="pickup">Recogida</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Metodo de pago</label>
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="bank_transfer">Transferencia</option>
                    <option value="cash">Efectivo</option>
                  </select>
                </div>
              </div>

              {/* Shipping quote */}
              <div className="flex items-center gap-3">
                <button onClick={calcShipping} className="text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-50">Calcular envio</button>
                {shippingQuote && (
                  <span className="text-sm text-gray-600">
                    Envio: <span className="font-medium">{shippingQuote.shippingCost} {shippingQuote.currency}</span> · ETA {shippingQuote.etaDays} dias
                  </span>
                )}
              </div>

              {/* Order notes */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Notas del pedido</label>
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px] resize-y"
                  placeholder="Indicaciones, observaciones o instrucciones especiales (opcional)"
                  maxLength={500}
                />
                <div className="text-[11px] text-gray-400 text-right mt-1">{orderNotes.length}/500</div>
              </div>

              {/* Order summary */}
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <div className="text-sm font-medium">Resumen del pedido</div>

                {/* Client info */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Cliente:</span>
                  <span className="font-medium">
                    {isDistributor ? recipient.name : customer.name}
                    {selectedClientTarifa && <span className="text-blue-600 ml-2">Tarifa {selectedClientTarifa}</span>}
                  </span>
                </div>

                {/* Drop-shipping info */}
                {isDistributor && dropShippingEnabled && (
                  <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1.5 rounded">
                    Envio directo a: {recipient.name} {notifyRecipient ? '(con email logistico)' : '(sin notificacion)'}
                  </div>
                )}

                {/* Products */}
                <div className="space-y-1">
                  {items.map((it) => {
                    const price = it.price || productPrices[it.productId] || 0;
                    return (
                      <div key={it.productId} className="flex justify-between text-sm">
                        <span className="text-gray-600 truncate mr-2">{it.name} x{it.quantity}</span>
                        <span className="font-medium shrink-0">{(price * it.quantity).toFixed(2)}&euro;</span>
                      </div>
                    );
                  })}
                </div>

                {/* Totals */}
                <div className="border-t pt-2 space-y-1">
                  {(() => {
                    const { subtotal, shippingCost, total } = calculateTotals();
                    return (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal:</span>
                          <span>{subtotal.toFixed(2)}&euro;</span>
                        </div>
                        {shippingCost > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Envio:</span>
                            <span>{shippingCost.toFixed(2)}&euro;</span>
                          </div>
                        )}
                        <div className="flex justify-between text-base font-semibold pt-1">
                          <span>Total:</span>
                          <span>{total.toFixed(2)}&euro;</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {!shippingQuote && (
                  <div className="text-xs text-amber-600 bg-amber-50/50 px-2 py-1.5 rounded">
                    Calcula el envio para ver el total final
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <div>
            {step > 1 && (
              <button onClick={goBack} className="px-4 py-2 text-sm border rounded-lg hover:bg-white">
                &larr; Anterior
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-white">Cancelar</button>
            {step < 3 ? (
              <button onClick={goNext} className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800">
                Siguiente &rarr;
              </button>
            ) : (
              <button onClick={createOrder} disabled={submitting} className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">
                {submitting ? 'Creando...' : 'Crear pedido'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrderModal;
